import {
  ESIM_UPSELL_TICKET_METADATA,
  type LoyaltyEsimUpsellTicketMetadata,
} from 'loyalty/constants/loyaltyEsimUpsellOptions'
import { getEsimGbForTier } from 'loyalty/lib/esims/loyaltyEsimUtils'

/** Checks if the id is a known upsell SKU. */
export function isEsimUpsellTicketId(id: string): id is App.LoyaltyEsimUpsellTicketId {
  return Object.hasOwn(ESIM_UPSELL_TICKET_METADATA, id)
}

/** Maps a tier-included GB allowance to a fixed-data upsell ticket id when one exists. */
export function getEsimUpsellTicketIdFromIncludedGb(
  includedGb: number,
): App.LoyaltyEsimUpsellTicketId | undefined {
  const candidate = `${includedGb}GB`
  return isEsimUpsellTicketId(candidate) ? candidate : undefined
}

export function getEsimUpsellTicketMetadata<TicketId extends App.LoyaltyEsimUpsellTicketId>(
  ticketId: TicketId,
): (typeof ESIM_UPSELL_TICKET_METADATA)[TicketId] {
  return ESIM_UPSELL_TICKET_METADATA[ticketId]
}

/** Maps a loyalty package-size SKU to upgrade metadata, when one exists. */
function getEsimUpsellTicketMetadataFromPackageSize(
  packageSize: string | null | undefined,
): LoyaltyEsimUpsellTicketMetadata | null {
  if (!packageSize || !isEsimUpsellTicketId(packageSize)) return null
  return getEsimUpsellTicketMetadata(packageSize)
}

/** Paid upgrade on an order — unlimited is always paid; fixed-data only when above tier inclusion. */
export function getEsimPurchasedUpsellFromOrderBenefit(
  esimBenefit: App.OrderLoyaltyEsimBenefit,
  loyaltyConfig: App.LoyaltyConfig,
): LoyaltyEsimUpsellTicketMetadata | undefined {
  const option = getEsimUpsellTicketMetadataFromPackageSize(esimBenefit.packageSize)
  if (!option) return undefined

  if (option.kind === 'unlimited_days') return option

  const tierGb = getEsimGbForTier(loyaltyConfig, esimBenefit.tier)
  if (tierGb !== undefined && option.gb > tierGb) return option

  return undefined
}

type EsimUpsellTicketLike = {
  id?: string
  ticketId?: string
  bookingOptions?: {
    toESimDataPackage?: string
    fromESimDataPackage?: string
  }
}

function parseEsimUpsellTicketId(
  rawId: string | null | undefined,
): App.LoyaltyEsimUpsellTicketId | null {
  if (!rawId) return null
  return isEsimUpsellTicketId(rawId) ? rawId : null
}

export function getEsimUpsellTicketIdsFromTicket(
  ticket: EsimUpsellTicketLike,
): {
  toTicketId: App.LoyaltyEsimUpsellTicketId | null
  fromTicketId: App.LoyaltyEsimUpsellTicketId | null
} {
  const toRaw = ticket.bookingOptions?.toESimDataPackage ?? ticket.id ?? ticket.ticketId
  const fromRaw = ticket.bookingOptions?.fromESimDataPackage

  return {
    toTicketId: parseEsimUpsellTicketId(toRaw),
    fromTicketId: parseEsimUpsellTicketId(fromRaw),
  }
}
